// client/src/api/emailTemplates.js
// Frontend API service for email templates

const API_BASE_URL = '/api/email-templates';

/**
 * Fetch all effective email templates for the current user
 */
export const getEmailTemplates = async () => {
    const response = await fetch(API_BASE_URL, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch email templates');
    }

    return response.json();
};

/**
 * Fetch email template by type
 */
export const getEmailTemplateByType = async (type) => {
    const response = await fetch(`${API_BASE_URL}/type/${type}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch email template');
    }

    return response.json();
};

/**
 * Fetch single email template by ID
 */
export const getEmailTemplateById = async (templateId) => {
    const response = await fetch(`${API_BASE_URL}/${templateId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch email template');
    }

    return response.json();
};

/**
 * Create a new user email template
 */
export const createEmailTemplate = async (templateData) => {
    const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(templateData)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create email template');
    }

    return response.json();
};

/**
 * Update an existing email template
 * If updating a system default, creates a user override
 */
export const updateEmailTemplate = async (templateId, updateData) => {
    const response = await fetch(`${API_BASE_URL}/${templateId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(updateData)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update email template');
    }

    return response.json();
};

/**
 * Delete a user email template (reverts to system default)
 */
export const deleteEmailTemplate = async (templateId) => {
    const response = await fetch(`${API_BASE_URL}/${templateId}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete email template');
    }

    return response.json();
};

/**
 * Preview an email template with placeholders replaced
 */
export const previewEmailTemplate = async (templateType, contextData) => {
    const response = await fetch(`${API_BASE_URL}/preview`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ templateType, contextData })
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to preview email template');
    }

    return response.json();
};

/**
 * Send an email using a template
 */
export const sendTemplateEmail = async (emailData) => {
    const {
        recipientEmail,
        templateType,
        contextData,
        quoteId,
        opportunityId,
        isAutomatic,
        subjectOverride,
        bodyOverride
    } = emailData;

    const response = await fetch(`${API_BASE_URL}/send`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
            recipientEmail,
            templateType,
            contextData,
            quoteId,
            opportunityId,
            isAutomatic,
            subjectOverride,
            bodyOverride
        })
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send email');
    }

    return response.json();
};

/**
 * Get available placeholders for each template type
 * This is a frontend helper to guide users on what placeholders they can use
 */
export const getPlaceholdersForType = (type) => {
    const placeholderMap = {
        'appointment_request_received': [
            { key: 'client_name', description: 'Client\'s name' },
            { key: 'project_name', description: 'Name of the project' },
            { key: 'preferred_date', description: 'Requested appointment date' },
            { key: 'company_name', description: 'Your company name' }
        ],
        'appointment_scheduled': [
            { key: 'client_name', description: 'Client\'s name' },
            { key: 'project_name', description: 'Name of the project' },
            { key: 'appointment_date', description: 'Scheduled appointment date' },
            { key: 'appointment_time', description: 'Scheduled appointment time' },
            { key: 'appointment_location', description: 'Appointment location/address' },
            { key: 'company_name', description: 'Your company name' }
        ],
        'appointment_rescheduled': [
            { key: 'client_name', description: 'Client\'s name' },
            { key: 'project_name', description: 'Name of the project' },
            { key: 'appointment_date', description: 'New appointment date' },
            { key: 'appointment_time', description: 'New appointment time' },
            { key: 'appointment_location', description: 'Appointment location/address' },
            { key: 'company_name', description: 'Your company name' }
        ],
        'quote_sent': [
            { key: 'client_name', description: 'Client\'s name' },
            { key: 'project_name', description: 'Name of the project' },
            { key: 'quote_link', description: 'Link to view the quote' },
            { key: 'company_name', description: 'Your company name' }
        ],
        'quote_followup': [
            { key: 'client_name', description: 'Client\'s name' },
            { key: 'project_name', description: 'Name of the project' },
            { key: 'quote_link', description: 'Link to view the quote' },
            { key: 'company_name', description: 'Your company name' }
        ],
        'quote_accepted': [
            { key: 'client_name', description: 'Client\'s name' },
            { key: 'project_name', description: 'Name of the project' },
            { key: 'quote_total', description: 'Total quote amount' },
            { key: 'company_name', description: 'Your company name' }
        ],
        'quote_rejected': [
            { key: 'client_name', description: 'Client\'s name' },
            { key: 'project_name', description: 'Name of the project' },
            { key: 'company_name', description: 'Your company name' }
        ],
        'quote_revision_requested': [
            { key: 'client_name', description: 'Client\'s name' },
            { key: 'project_name', description: 'Name of the project' },
            { key: 'revision_details', description: 'Details of requested revisions' },
            { key: 'company_name', description: 'Your company name' }
        ],
        'invoice_issued': [
            { key: 'client_name', description: 'Client\'s name' },
            { key: 'project_name', description: 'Name of the project' },
            { key: 'invoice_number', description: 'Invoice number' },
            { key: 'invoice_total', description: 'Total invoice amount' },
            { key: 'due_date', description: 'Payment due date' },
            { key: 'invoice_link', description: 'Link to view the invoice' },
            { key: 'company_name', description: 'Your company name' }
        ],
        'payment_confirmation': [
            { key: 'client_name', description: 'Client\'s name' },
            { key: 'project_name', description: 'Name of the project' },
            { key: 'payment_amount', description: 'Amount paid' },
            { key: 'payment_date', description: 'Date payment was received' },
            { key: 'payment_method', description: 'Payment method used' },
            { key: 'company_name', description: 'Your company name' }
        ]
    };

    return placeholderMap[type] || [];
};

/**
 * Check if the user's Gmail connection is still valid
 */
export const checkGmailConnection = async () => {
    const response = await fetch('/api/users/auth/gmail/check', {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to verify Gmail connection');
    }

    return response.json();
};

